/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useCallback, useState} from "react"
import {useTranslation} from "react-i18next"
import cx from "classnames"
import {isEmpty} from "lodash"
import debounce from "debounce-promise"
import {Icon} from "components/Icon"
import {Search} from "components/inputs/Search"
import {BaseButton} from "components/buttons"
import {BaseFilterContext} from "./BaseFilterContext"
import styles from "./BaseNewFilter.module.css"
import {FilterKey, SavedFilter} from "types/filter"
import {useVisible} from "hooks/useVisible"
import {SavedFiltersSelect, SavedFiltersPopup, SaveFilterForm} from "uiKit/KlassappFilters/parts"

type BaseNewFilterProps = {
    className?: string
    filter?: any
    children?: JSX.Element
    searchValue?: string
    showRightFilter?: boolean
    customHeaderFilter?: any
    onClickClear?: () => any
    onClick?: () => any
    onSearchInput: (input: string) => any
    renderRightFilter?: () => JSX.Element
    showSearch?: boolean
    savedFilterOptions?: {
        enabled: boolean
        filterKey: FilterKey
        setFilter: (_filter: any) => void
    }
}

export function BaseNewFilter(props: BaseNewFilterProps) {
    const {
        children,
        className,
        filter = {},
        searchValue,
        showRightFilter = true,
        onClickClear,
        onClick,
        onSearchInput,
        renderRightFilter,
        showSearch = true,
        savedFilterOptions,
        customHeaderFilter
    } = props
    const {t} = useTranslation(["common"])
    const [isShowFilter, setIsShowFilter] = React.useState(false)
    const [hasFilterBody, setHasFilterBody] = React.useState(false)
    const [search, setSearch] = React.useState("")
    const [selectedSavedFilter, setSelectedSavedFilter] = useState<SavedFilter | null>(null)
    const savedFilterForm = useVisible(false)
    const [savedFilterSelectKey, setSavedFilterSelectKey] = useState(new Date().toString())
    const manageFilterPopup = useVisible(false)

    useEffect(() => {
        setSearch(searchValue)
    }, [searchValue])

    useEffect(() => {
        checkHasFilterBody()
    }, [filter])

    const checkHasFilterBody = () => {
        const hasFilter = Object.values(filter).some((value) => {
            if ((typeof value === "boolean" && value) || !isEmpty(value)) {
                return true
            }
            return false
        })
        setHasFilterBody(hasFilter)
    }

    const toggleShowBodyFilter = () => {
        setIsShowFilter(!isShowFilter)
    }

    const debounceSearch = useCallback(
        debounce((searchInput) => {
            onSearchInput(searchInput)
        }, 500),
        []
    )

    const onSearch = (search) => {
        setSearch(search)
        debounceSearch(search)
    }

    const onClickApply = () => {
        setIsShowFilter(false)
        onClick()
    }

    const handleSelectingSavedFilter = (_savedFilter: SavedFilter) => {
        setSelectedSavedFilter(_savedFilter)
        let _filters =
            typeof _savedFilter?.content === "string" ? JSON.parse(_savedFilter?.content) : _savedFilter?.content
        savedFilterOptions?.setFilter(_filters)
    }

    const onSavedFilterSuccess = () => {
        savedFilterForm.close()
        setSavedFilterSelectKey(new Date().toString())
    }

    const renderSaveFilterForm = () => {
        if (!savedFilterOptions?.enabled) return <div />

        if (savedFilterForm.isVisible) {
            return (
                <SaveFilterForm
                    filterKey={savedFilterOptions?.filterKey}
                    filters={filter}
                    onValidateFilters={() => true}
                    onSaved={onSavedFilterSuccess}
                    onCancel={savedFilterForm.close}
                    currentSavedFilter={selectedSavedFilter}
                />
            )
        }

        return <BaseButton variant="secondary" title="Save Filter" onClick={savedFilterForm.open} />
    }

    return (
        <BaseFilterContext.Provider value={{isShowFilter, setIsShowFilter}}>
            <div className={cx(styles.wrap, className)}>
                {!isEmpty(children) && isShowFilter && (
                    <div className={styles.body}>
                        <div className={styles.headerBody}>
                            <div className={styles.sharedFilterWrap}>
                                <div className={styles.headerFilterIconWrap} onClick={toggleShowBodyFilter}>
                                    <Icon icon="FILTER" color="#fff" />
                                </div>
                                {savedFilterOptions?.enabled && (
                                    <div className={styles.savedBox}>
                                        <span className={styles.selectLabel}>Select a filter</span>
                                        <div onClick={(e) => e.stopPropagation()} className={styles.manageFilterWrap}>
                                            <SavedFiltersSelect
                                                isClearable
                                                key={savedFilterSelectKey}
                                                value={selectedSavedFilter ?? null}
                                                onChange={handleSelectingSavedFilter}
                                                filterKey={savedFilterOptions?.filterKey}
                                            />
                                            <div className={styles.link} onClick={manageFilterPopup.open}>
                                                Manage filter
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {customHeaderFilter ? customHeaderFilter() : null}
                            </div>
                            <div className={styles.closeIconWrap} onClick={toggleShowBodyFilter}>
                                <Icon icon="CLOSE" className={styles.closeIcon} />
                            </div>
                        </div>
                        <div className={styles.bodyContent}>{children}</div>
                        <div className={styles.footerBody}>
                            {renderSaveFilterForm()}
                            <div className={styles.footerBodyAction}>
                                <BaseButton
                                    title={t("common:action.clear")}
                                    variant="secondary"
                                    onClick={onClickClear}
                                />
                                <BaseButton title={t("common:action.apply")} onClick={onClickApply} />
                            </div>
                        </div>
                    </div>
                )}
                <div className={styles.filterWrap}>
                    <div className={styles.leftFilterWrap}>
                        {!isEmpty(children) && !isShowFilter && (
                            <div
                                className={cx(styles.filterIconWrap, {
                                    [styles.hasFilterBody]: hasFilterBody
                                })}
                                onClick={toggleShowBodyFilter}>
                                <Icon icon="FILTER" color={hasFilterBody ? "#fff" : "#1e90ff"} />
                            </div>
                        )}
                        {showSearch && (
                            <Search
                                className={styles.search}
                                onChange={onSearch}
                                value={search}
                                placeholder={t("common:action.search")}
                            />
                        )}
                    </div>
                    {showRightFilter && (
                        <div className={styles.rightFilterWrap}>{renderRightFilter && renderRightFilter()}</div>
                    )}
                </div>
            </div>
            {savedFilterOptions?.enabled && (
                <SavedFiltersPopup
                    isShow={manageFilterPopup.isVisible}
                    onClose={() => {
                        setSavedFilterSelectKey(new Date().toString())
                        manageFilterPopup.close()
                    }}
                    filterKey={savedFilterOptions?.filterKey}
                />
            )}
        </BaseFilterContext.Provider>
    )
}
